import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, serverTimestamp, query, where, limit, runTransaction, addDoc } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Plus, Trash2, Search, X, CheckCircle, Save, Send, Loader2, ClipboardCheck, UserCheck, Truck, ArrowDownLeft, FileText, Building2 } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';
import { NewCustomerModal, AddAddressModal } from './CustomerModals.js';
import { NewWarehouseModal } from './WarehouseModals.js';
import { NewVendorModal } from './VendorModals.js';
import MicInwardDetails from './components/MicInwardDetails.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';

const CreateMICPage = ({ db, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { micId } = useParams();
  const initialDispatchId = location.state?.dispatchId;

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchingPC, setFetchingPC] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [bomDatabase, setBomDatabase] = useState([]);
  
  const [allCustomers, setAllCustomers] = useState([]); 
  const [selectedCustomer, setSelectedCustomer] = useState(null); 

  const [allVendors, setAllVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [allWarehouses, setAllWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null); // Destination

  const [selectedSourceWarehouse, setSelectedSourceWarehouse] = useState(null); // Source (for Interbranch)

  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showNewWarehouseModal, setShowNewWarehouseModal] = useState(false);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [pcList, setPcList] = useState([]);
  const [mocList, setMocList] = useState([]);
  
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('shipping');
  const [addressDataToEdit, setAddressDataToEdit] = useState(null);

  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    return `${addr.attention ? addr.attention + ', ' : ''}${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
  };

  const [formData, setFormData] = useState({
    micDate: new Date().toISOString().split('T')[0],
    inwardNo: '',
    micNo: '',
    typeOfMIC: '', 
    referenceNo: '', 
    customerName: '',
    customerID: '',
    vendorID: '',
    sourceWarehouseID: '',
    shippingAddress: '',
    warehouse: '',
    components: [],
    preparedBy: '',
    quantityVerifiedBy: '',
    vehicleNumber: '',
    vehicleInTime: '',
    vehicleOutTime: '',
    vehicleDateTime: new Date().toISOString().slice(0, 16),
    verifiedByFinance: '', 
    declarationDate: new Date().toISOString().split('T')[0],
    micStatus: 'draft',
    micRemarks: '',
    completionRemarks: '', 
    completionFiles: [], 
    micCompletionRemarks: '',
    micCompletionFiles: [],
  });
  const [selectedMicCompletionFile, setSelectedMicCompletionFile] = useState(null);

  const [pcSearchTerm, setPcSearchTerm] = useState('');
  const [pcSuggestions, setPcSuggestions] = useState([]);
  const [showPcSuggestions, setShowPcSuggestions] = useState(false);
  const pcSearchRef = useClickOutside(() => setShowPcSuggestions(false));
  const isSalesRentalMic = formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental Return';

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const bomSnap = await getDocs(collection(db, "bom_products"));
        setBomDatabase(bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const [custSnap, pcSnap, mocSnap, whSnap, venSnap] = await Promise.all([
            getDocs(collection(db, "customers")),
            getDocs(query(collection(db, "inwards"), where("status", "==", "pc_completed"), limit(50))),
            getDocs(query(collection(db, "dispatches"), where("dispatchStatus", "==", "delivered"), limit(50))),
            getDocs(collection(db, "warehouses")),
            getDocs(collection(db, "vendors"))
        ]);

        const customers = custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCustomers(customers);
        setPcList(pcSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setMocList(mocSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const warehouses = whSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllWarehouses(warehouses);

        const vendors = venSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllVendors(vendors);

        if (micId) {
          const micDoc = await getDoc(doc(db, "inwards", micId));
          if (micDoc.exists()) {
            const data = micDoc.data();

            let initialComponents = data.micComponents || [];
            if (initialComponents.length === 0 && data.components) {
                initialComponents = data.components.map(c => ({
                    id: `${c.sku}-${Date.now()}`,
                    sku: c.sku,
                    name: c.name || c.description || '',
                    newQty: 0,
                    rentalQty: 0,
                    repairableQty: 0,
                    nonRepairableQty: 0,
                    weight: c.weight || 0,
                    originalPcQuantity: Number(c.totalLifted || c.quantity || 0)
                }));
            }

            setFormData({
                ...data,
                id: micDoc.id,
                micDate: data.micDate || new Date().toISOString().split('T')[0],
                inwardNo: data.inwardNo || data.micNo || '',
                micNo: data.micNo || data.inwardNo || '',
                typeOfMIC: data.typeOfMIC || (data.type === 'Rental Return' ? 'Rental Return' : (data.type === 'Sale Return' ? 'Sale Return' : '')),
                referenceNo: data.referenceNo || data.inwardNo || '',
                customerName: data.customerName || '',
                customerID: data.customerID || '',
                vendorID: data.vendorID || '',
                sourceWarehouseID: data.sourceWarehouseID || '',
                shippingAddress: data.shippingAddress || data.siteAddress || '',
                warehouse: data.warehouse || '',
                components: initialComponents,
                micStatus: data.micStatus || 'draft',
                preparedBy: data.preparedBy || user?.displayName || user?.email || '',
                declarationDate: data.declarationDate || new Date().toISOString().split('T')[0],
                completionRemarks: data.completionRemarks || '',
                micRemarks: data.micRemarks || '',
                completionFiles: data.completionFiles || [],
                micCompletionRemarks: data.mic_completionRemarks || data.micCompletionRemarks || '',
                micCompletionFiles: data.mic_completionFiles || data.micCompletionFiles || [],
            });
            if (data.referenceNo) setPcSearchTerm(data.referenceNo);
            if (data.customerID) {
                const foundCust = customers.find(c => c.id === data.customerID);
                setSelectedCustomer(foundCust);
            }
            if (data.vendorID) {
                const foundVendor = vendors.find(v => v.id === data.vendorID);
                setSelectedVendor(foundVendor);
            }
            if (data.sourceWarehouseID) {
                const foundSrcWh = warehouses.find(w => w.id === data.sourceWarehouseID);
                setSelectedSourceWarehouse(foundSrcWh);
            }
            if (data.warehouse) {
                const foundWh = warehouses.find(w => w.name === data.warehouse);
                setSelectedWarehouse(foundWh);
            }
          }
        } else {
          const generatedInwardNo = await getNextInwardNumber(db);
          setFormData(prev => ({ ...prev, inwardNo: generatedInwardNo, micNo: generatedInwardNo }));

          if (initialDispatchId) {
            const selectedDispatch = mocSnap.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .find(dispatch => dispatch.id === initialDispatchId);

            if (selectedDispatch) {
              prefillMicFromDispatch(selectedDispatch, customers, warehouses, vendors);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
    } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, initialDispatchId, micId, user]);

  const prefillMicFromDispatch = (dispatchData, customers, warehouses, vendors) => {
    const isInterbranch = dispatchData.typeOfMOC === 'Interbranch Transfer';
    const isRental = dispatchData.typeOfMOC === 'Rental';
    const micType = isInterbranch ? 'Interbranch Transfer' : (isRental ? 'Rental Return' : 'Sale Return');
    const referenceNo = dispatchData.challanNo || dispatchData.mocNo || '';
    const salesShippingSnapshot = !isInterbranch ? getSalesShippingSnapshot(dispatchData) : null;
    const billingAddress = typeof dispatchData.billToAddress === 'string'
      ? dispatchData.billToAddress
      : formatAddress(dispatchData.billToAddress);
    const shippingAddress = isInterbranch
      ? (typeof dispatchData.shipToAddress === 'string'
          ? dispatchData.shipToAddress
          : formatAddress(dispatchData.shipToAddress))
      : getShippingSnapshotAddress(salesShippingSnapshot, dispatchData.shipToAddress || dispatchData.shippingAddress || '');

    setFormData(prev => ({
      ...prev,
      typeOfMIC: micType,
      referenceNo,
      micNo: prev.micNo,
      customerName: isInterbranch ? (dispatchData.warehouse || '') : (dispatchData.customerName || ''),
      customerID: isInterbranch ? '' : (dispatchData.customerID || ''),
      vendorID: '',
      sourceWarehouseID: isInterbranch ? (dispatchData.sourceWarehouseID || '') : '',
      shippingAddress: isInterbranch ? billingAddress : shippingAddress,
      warehouse: isInterbranch ? (dispatchData.customerName || dispatchData.destinationWarehouseName || '') : (dispatchData.warehouse || ''),
      components: (dispatchData.components || []).map(comp => {
        const totalDCQty = Number(comp.newQty || 0) + Number(comp.rentalQty || 0);
        return {
          id: `${comp.sku || 'item'}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          sku: comp.sku || '',
          name: comp.name || comp.description || '',
          newQty: isInterbranch ? Number(comp.newQty || 0) : (isRental ? 0 : totalDCQty),
          rentalQty: isInterbranch ? Number(comp.rentalQty || 0) : (isRental ? totalDCQty : 0),
          repairableQty: 0,
          nonRepairableQty: 0,
          weight: Number(comp.weight || 0),
          originalPcQuantity: totalDCQty
        };
      })
    }));

    if (!isInterbranch && dispatchData.customerID) {
      const foundCustomer = customers.find(customer => customer.id === dispatchData.customerID);
      if (foundCustomer) setSelectedCustomer(foundCustomer);
    }

    if (dispatchData.vendorID) {
      const foundVendor = vendors.find(vendor => vendor.id === dispatchData.vendorID);
      if (foundVendor) setSelectedVendor(foundVendor);
    }

    if (isInterbranch) {
      const foundSourceWarehouse = warehouses.find(warehouse => warehouse.id === dispatchData.sourceWarehouseID || warehouse.name === dispatchData.warehouse);
      if (foundSourceWarehouse) setSelectedSourceWarehouse(foundSourceWarehouse);

      const foundDestinationWarehouse = warehouses.find(warehouse =>
        warehouse.name === dispatchData.customerName || warehouse.name === dispatchData.destinationWarehouseName
      );
      if (foundDestinationWarehouse) setSelectedWarehouse(foundDestinationWarehouse);
    } else if (dispatchData.warehouse) {
      const foundWarehouse = warehouses.find(warehouse => warehouse.name === dispatchData.warehouse);
      if (foundWarehouse) setSelectedWarehouse(foundWarehouse);
    }

    setPcSearchTerm(referenceNo);
  };

  const getNextInwardNumber = async (db) => {
    const sequencesRef = doc(db, "counters", "inward_sequence");
    try {
      const result = await runTransaction(db, async (transaction) => {
        const sequenceDoc = await transaction.get(sequencesRef);
        let currentCount = 2000;
        if (sequenceDoc.exists() && sequenceDoc.data().count !== undefined) {
          currentCount = sequenceDoc.data().count;
        }
        const nextCount = currentCount + 1;
        transaction.set(sequencesRef, { count: nextCount }, { merge: true });
        return currentCount.toString();
      });
      return result;
    } catch (error) {
      console.error("Error getting next inward number: ", error);
      return "TEMP-INWARD";
    }
  };

  const handlePcSearch = (val) => {
    setPcSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      let matches = [];
      
      if (formData.typeOfMIC === 'Interbranch Transfer') {
        matches = mocList.filter(moc => 
          (moc.challanNo || '').toLowerCase().includes(term) ||
          (moc.mocNo || '').toLowerCase().includes(term) ||
          (moc.customerName || '').toLowerCase().includes(term)
        );
      } else {
        matches = pcList.filter(pc => 
          (pc.pickupChallanNo || '').toLowerCase().includes(term) ||
          (pc.inwardNo || '').toLowerCase().includes(term) ||
          (pc.customerName || '').toLowerCase().includes(term)
        );
      }
      
      setPcSuggestions(matches.slice(0, 10));
      setShowPcSuggestions(true);
    } else {
      setShowPcSuggestions(false);
    }
  };

  const handlePcSelection = (docData) => {
    setFetchingPC(true);
    setShowPcSuggestions(false);
    
    const isInterbranch = formData.typeOfMIC === 'Interbranch Transfer';
    const pickerDisplayNo = isInterbranch
      ? (docData.challanNo || docData.mocNo || '')
      : (docData.pickupChallanNo || docData.inwardNo || '');
    const referenceNo = isInterbranch
      ? (docData.challanNo || docData.mocNo || '')
      : (docData.referenceNo || docData.challanNo || docData.mocNo || pickerDisplayNo || '');
    setPcSearchTerm(pickerDisplayNo);
    
    let customerName = docData.customerName || '';
    let shippingAddress = '';
    
    if (isInterbranch) {
        // For Interbranch MIC:
        // Source of MIC = Source Warehouse of MOC (docData.warehouse)
        // Destination of MIC = Destination Warehouse of MOC (docData.customerName)
        customerName = docData.warehouse || ''; 
        shippingAddress = docData.sourceWarehouseAddress || ''; 
    } else {
        shippingAddress = docData.siteAddress || docData.billingAddress || '';
    }

    let preselectedType = formData.typeOfMIC;
    if (!isInterbranch) {
        if (docData.type === 'Rental Return') preselectedType = 'Rental Return';
        else if (docData.type === 'Sale Return') preselectedType = 'Sale Return';
    }

    setFormData(prev => ({
      ...prev,
      inwardNo: isInterbranch ? (prev.inwardNo || prev.micNo || '') : (docData.inwardNo || prev.inwardNo || ''),
      micNo: isInterbranch ? prev.micNo : (docData.inwardNo || prev.micNo || ''),
      customerID: docData.customerID || '',
      vendorID: docData.vendorID || '',
      sourceWarehouseID: isInterbranch ? (docData.sourceWarehouseID || '') : '',
      referenceNo: referenceNo,
      typeOfMIC: preselectedType,
      customerName: customerName,
      shippingAddress: shippingAddress,
      warehouse: isInterbranch ? (docData.customerName || prev.warehouse || '') : (docData.warehouse || prev.warehouse || ''),
      components: (docData.components || []).map(comp => {
        const sNew = Number(comp.newQty || 0);
        const sRental = Number(comp.rentalQty || 0);
        const liftedQty = Number(comp.totalLifted || 0);
        const sQty = Number(comp.quantity || 0);
        const totalRef = isInterbranch ? (sNew + sRental) : sQty;
        const materialQty = isInterbranch ? totalRef : liftedQty;

        return {
          id: comp.id || `${comp.sku}-${Date.now()}`,
          sku: comp.sku || '',
          name: comp.name || comp.description || '',
          newQty: isInterbranch ? sNew : 0,
          rentalQty: isInterbranch ? sRental : 0,
          repairableQty: 0,
          nonRepairableQty: 0,
          weight: comp.weight || 0,
          originalPcQuantity: materialQty
        };
      })
    }));

    if (docData.customerID && !isInterbranch) {
        const foundCust = allCustomers.find(c => c.id === docData.customerID);
        if (foundCust) setSelectedCustomer(foundCust);
    }

    if (docData.vendorID) {
        const foundVen = allVendors.find(v => v.id === docData.vendorID);
        if (foundVen) setSelectedVendor(foundVen);
    }

    if (isInterbranch) {
        // Source Warehouse of MOC
        const foundSrcWh = allWarehouses.find(w => w.name === docData.warehouse);
        if (foundSrcWh) setSelectedSourceWarehouse(foundSrcWh);

        // Destination Warehouse of MOC (Target for MIC)
        const foundWh = allWarehouses.find(w => w.name === docData.customerName);
        if (foundWh) setSelectedWarehouse(foundWh);
    } else if (docData.warehouse) {
        const foundWh = allWarehouses.find(w => w.name === docData.warehouse);
        setSelectedWarehouse(foundWh);
    }
    setFetchingPC(false);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    const firstShipping = customer.addresses?.find(a => a.address_type === 'Shipping');
    setFormData(prev => ({ 
        ...prev, 
        customerName: customer.display_name, 
        customerID: customer.id,
        shippingAddress: firstShipping ? formatAddress(firstShipping) : '',
    }));
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setFormData(prev => ({ 
        ...prev, 
        customerName: vendor.name, 
        vendorID: vendor.id,
        shippingAddress: vendor.address ? `${vendor.address}, ${vendor.city || ''}, ${vendor.state || ''} - ${vendor.pincode || ''}` : ''
    }));
  };

  const selectSourceWarehouse = (warehouse) => {
    setSelectedSourceWarehouse(warehouse);
    setFormData(prev => ({ 
        ...prev, 
        customerName: warehouse.name, 
        sourceWarehouseID: warehouse.id,
        shippingAddress: warehouse.address ? `${warehouse.address}, ${warehouse.city || ''}, ${warehouse.state || ''} - ${warehouse.pincode || ''}` : ''
    }));
  };

  const selectWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setFormData(prev => ({ 
      ...prev, 
      warehouse: warehouse.name
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValidationErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getFieldStyle = (fieldName, extraStyle = {}) => ({
    ...extraStyle,
    ...(validationErrors[fieldName] ? errorInputStyle : {})
  });

  const validateBeforeConfirm = () => {
    const nextErrors = {};

    if (!String(formData.customerName || '').trim()) nextErrors.customerName = true;
    if (!String(formData.warehouse || '').trim()) nextErrors.warehouse = true;
    if (!String(formData.preparedBy || '').trim()) nextErrors.preparedBy = true;
    if (!String(formData.quantityVerifiedBy || '').trim()) nextErrors.quantityVerifiedBy = true;
    if (!String(formData.vehicleNumber || '').trim()) nextErrors.vehicleNumber = true;
    if (!String(formData.vehicleDateTime || '').trim()) nextErrors.vehicleDateTime = true;
    if (!String(formData.vehicleInTime || '').trim()) nextErrors.vehicleInTime = true;
    if (!String(formData.vehicleOutTime || '').trim()) nextErrors.vehicleOutTime = true;
    if (!String(formData.verifiedByFinance || '').trim()) nextErrors.verifiedByFinance = true;
    if (!String(formData.declarationDate || '').trim()) nextErrors.declarationDate = true;
    if (!String(formData.micCompletionRemarks || '').trim()) nextErrors.micCompletionRemarks = true;
    if (!selectedMicCompletionFile && !(formData.micCompletionFiles || []).length) nextErrors.micCompletionFiles = true;

    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleComponentChange = (index, field, value) => {
    const updated = [...formData.components];
    const val = field === 'weight' ? parseFloat(value) || 0 : parseInt(value) || 0;

    const comp = updated[index];
    if (comp.originalPcQuantity !== undefined && ['newQty', 'rentalQty', 'repairableQty', 'nonRepairableQty'].includes(field)) {
      const total = (field === 'newQty' ? val : (parseInt(comp.newQty) || 0)) +
                    (field === 'rentalQty' ? val : (parseInt(comp.rentalQty) || 0)) +
                    (field === 'repairableQty' ? val : (parseInt(comp.repairableQty) || 0)) +
                    (field === 'nonRepairableQty' ? val : (parseInt(comp.nonRepairableQty) || 0));
      if (total > comp.originalPcQuantity) {
        const refType = formData.typeOfMIC === 'Interbranch Transfer' ? 'DC' : 'PC';
        alert(`Error: The total quantity for ${comp.name} (${total}) cannot exceed the original ${refType} quantity (${comp.originalPcQuantity}).`);
        return;
      }
    }

    updated[index][field] = val;
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const deleteComponent = (index) => {
    const updated = formData.components.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const addComponent = (product) => {
    const newComp = {
      id: `${product.sku}-${Date.now()}`,
      sku: product.sku,
      name: product.name,
      newQty: 0,
      rentalQty: 0,
      repairableQty: 0,
      nonRepairableQty: 0,
      weight: product.weight || 0
    };
    setFormData(prev => ({ ...prev, components: [...prev.components, newComp] }));
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleSearch = (val) => {
    setSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = bomDatabase.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.sku.toLowerCase().includes(term)
      ).slice(0, 10);
      setFilteredProducts(matches);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const totals = useMemo(() => {
    return formData.components.reduce((acc, c) => {
      acc.newQty += (parseInt(c.newQty) || 0);
      acc.rentalQty += (parseInt(c.rentalQty) || 0);
      acc.repairableQty += (parseInt(c.repairableQty) || 0);
      acc.nonRepairableQty += (parseInt(c.nonRepairableQty) || 0);
      const rowTotalQty = (parseInt(c.newQty) || 0) + (parseInt(c.rentalQty) || 0) + (parseInt(c.repairableQty) || 0) + (parseInt(c.nonRepairableQty) || 0);
      acc.weight += rowTotalQty * (parseFloat(c.weight) || 0);
      return acc;
    }, { newQty: 0, rentalQty: 0, repairableQty: 0, nonRepairableQty: 0, weight: 0 });
  }, [formData.components]);

  const saveMic = async (status) => {
    if (!formData.micNo) return alert("MIC Number is required.");
    if (status === 'pending_qc' && !validateBeforeConfirm()) {
      alert("Please fill all highlighted required fields before confirming the MIC.");
      return;
    }
    if (status === 'pending_qc' && !window.confirm("Are you sure you want to CONFIRM this MIC?")) return;

    setIsSaving(true);
    try {
      if (!db) throw new Error("Firestore database instance not found.");
      
      const timestamp = serverTimestamp();
      const userName = user?.displayName || user?.email || 'System User';

      for (const comp of formData.components) {
        if (comp.originalPcQuantity !== undefined) {
          const total = (parseInt(comp.newQty) || 0) + (parseInt(comp.rentalQty) || 0) + 
                        (parseInt(comp.repairableQty) || 0) + (parseInt(comp.nonRepairableQty) || 0);
          if (total > comp.originalPcQuantity) {
            const refType = formData.typeOfMIC === 'Interbranch Transfer' ? 'DC' : 'PC';
            alert(`Validation Error: Total quantity for ${comp.name} exceeds original ${refType} quantity.`);
            setIsSaving(false);
            return;
          }
        }
      }

      const sanitizedComponents = (formData.components || []).map(c => ({
        id: String(c.id || ''),
        sku: String(c.sku || ''),
        name: String(c.name || ''),
        newQty: Number(c.newQty) || 0,
        rentalQty: Number(c.rentalQty) || 0,
        repairableQty: Number(c.repairableQty) || 0,
        nonRepairableQty: Number(c.nonRepairableQty) || 0,
        weight: Number(c.weight) || 0,
        originalPcQuantity: Number(c.originalPcQuantity) || 0
      }));

      let micCompletionFiles = formData.micCompletionFiles || [];
      if (selectedMicCompletionFile) {
        const safeName = (selectedMicCompletionFile.name || 'file').replace(/[^\w.\-() ]+/g, '_');
        const path = `mic_completion/${micId || formData.micNo || Date.now()}/${Date.now()}_${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, selectedMicCompletionFile);
        const url = await getDownloadURL(storageRef);
        micCompletionFiles = [{
          name: selectedMicCompletionFile.name,
          type: selectedMicCompletionFile.type,
          size: selectedMicCompletionFile.size,
          url
        }];
      }

      const payload = {
        inwardNo: String(formData.inwardNo || formData.micNo || ''),
        micDate: String(formData.micDate || new Date().toISOString().split('T')[0]),
        micNo: String(formData.micNo || ''),
        typeOfMIC: String(formData.typeOfMIC || ''),
        referenceNo: String(formData.referenceNo || ''),
        micRemarks: String(formData.micRemarks || ''),
        customerName: String(formData.customerName || ''),
        customerID: String(formData.customerID || ''),
        vendorID: String(formData.vendorID || ''),
        sourceWarehouseID: String(formData.sourceWarehouseID || ''),
        shippingAddress: String(formData.shippingAddress || ''),
        warehouse: String(formData.warehouse || ''),
        micComponents: sanitizedComponents,
        micStatus: status === 'pending_qc' ? 'mic_completed' : String(status || 'draft'),
        status: status === 'pending_qc' ? 'mic_completed' : 'mic_draft',
        preparedBy: String(formData.preparedBy || ''),
        quantityVerifiedBy: String(formData.quantityVerifiedBy || ''),
        vehicleNumber: String(formData.vehicleNumber || ''),
        vehicleInTime: String(formData.vehicleInTime || ''),
        vehicleOutTime: String(formData.vehicleOutTime || ''),
        vehicleDateTime: String(formData.vehicleDateTime || ''),
        verifiedByFinance: String(formData.verifiedByFinance || ''),
        declarationDate: String(formData.declarationDate || ''),
        totalWeight: Number(totals.weight || 0),
        updatedAt: timestamp,
        updatedBy: String(userName || 'System User'),
        createdAt: (micId && formData.createdAt) ? formData.createdAt : timestamp,
        completionRemarks: String(formData.completionRemarks || ''),
        completionFiles: (formData.completionFiles || []).map(f => ({
            name: String(f.name || ''),
            url: String(f.url || ''),
            type: String(f.type || 'file')
        })),
        mic_completionRemarks: String(formData.micCompletionRemarks || ''),
        mic_completionFiles: (micCompletionFiles || []).map(f => ({
            name: String(f.name || ''),
            url: String(f.url || ''),
            type: String(f.type || 'file'),
            size: Number(f.size || 0)
        }))
      };

      // LOG FOR DEBUGGING
      console.log("MIC Save Payload:", payload);
      console.log("MIC ID:", micId);

      if (micId && micId !== 'new' && micId !== 'undefined') {
        const docRef = doc(db, "inwards", String(micId));
        await updateDoc(docRef, payload);
      } else {
        const collRef = collection(db, "inwards");
        await addDoc(collRef, payload);
      }

      alert(`MIC ${status === 'draft' ? 'Saved as Draft' : 'Confirmed'} Successfully`);
      navigate('/ops/mic');
    } catch (error) {
      console.error("Error saving MIC:", error);
      alert("Error: " + error.message);
    } finally {
      if (selectedMicCompletionFile) {
        setSelectedMicCompletionFile(null);
      }
      setIsSaving(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ArrowDownLeft size={28} color="#2563eb" /> {micId ? 'Edit' : 'Create'} Material In Checklist
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {isSaving && <Loader2 className="animate-spin" size={24} color="#2563eb" />}
            <button onClick={() => saveMic('draft')} disabled={isSaving} style={draftBtnStyle}><Save size={18}/> Save Draft</button>
            <button onClick={() => saveMic('pending_qc')} disabled={isSaving} style={confirmBtnStyle}><Send size={18}/> Confirm MIC</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Document Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            <div><label style={labelStyle}>Date</label><input type="date" name="micDate" value={formData.micDate} onChange={handleChange} readOnly={isSalesRentalMic} style={{ ...inputStyle, backgroundColor: isSalesRentalMic ? '#f8fafc' : '#fff' }} /></div>
            
            <div>
                <label style={labelStyle}>Type of MIC</label>
                <select name="typeOfMIC" value={formData.typeOfMIC} onChange={handleChange} disabled={isSalesRentalMic} style={{ ...inputStyle, backgroundColor: isSalesRentalMic ? '#f8fafc' : '#fff' }}>
                    <option value="">Select Type...</option>
                    <option value="Sale Return">Sale Return</option>
                    <option value="Rental Return">Rental Return</option>
                    <option value="Purchase">Purchase</option>
                    <option value="Interbranch Transfer">Interbranch Transfer</option>
                </select>
            </div>

            {/* Document No. Picker */}
            <div style={{ position: 'relative' }} ref={pcSearchRef}>
                <label style={labelStyle}>
                    {!formData.typeOfMIC ? 'Document No. Picker' : 
                     (formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental Return' ? 'Pickup Challan (PC) No.' : 
                      formData.typeOfMIC === 'Interbranch Transfer' ? 'Interbranch MOC Picker' : 'Purchase Order No.')}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
                    <Search size={18} color="#64748b" />
                    <input 
                        type="text" 
                        name="referenceNo"
                        placeholder="Search or enter number..." 
                        value={pcSearchTerm} 
                        onChange={(e) => {
                            setPcSearchTerm(e.target.value);
                            if (formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental Return' || formData.typeOfMIC === 'Interbranch Transfer') handlePcSearch(e.target.value);
                        }} 
                        onFocus={() => (formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental Return' || formData.typeOfMIC === 'Interbranch Transfer') && pcSearchTerm.length > 1 && setShowPcSuggestions(true)}
                        style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px', backgroundColor: isSalesRentalMic ? '#f8fafc' : '#fff' }}
                        readOnly={isSalesRentalMic}
                    />
                    {fetchingPC && <Spinner size="small" />}
                </div>
                {showPcSuggestions && (
                    <div style={suggestionsBoxStyle}>
                        {pcSuggestions.map(pc => (
                            <div key={pc.id} onClick={() => handlePcSelection(pc)} style={suggestionItemStyle}>
                                <div style={{ fontWeight: 'bold' }}>{pc.pickupChallanNo || pc.inwardNo || pc.challanNo || pc.mocNo}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>{pc.customerName}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div><label style={labelStyle}>MIC No.</label><input type="text" name="micNo" value={formData.micNo} readOnly style={{ ...inputStyle, backgroundColor: '#f8fafc' }} /></div>
          </div>
        </div>

        <MicInwardDetails
          formData={formData}
          setFormData={setFormData}
          allCustomers={allCustomers}
          allVendors={allVendors}
          allWarehouses={allWarehouses}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
          selectedVendor={selectedVendor}
          setSelectedVendor={setSelectedVendor}
          selectedSourceWarehouse={selectedSourceWarehouse}
          setSelectedSourceWarehouse={setSelectedSourceWarehouse}
          selectedWarehouse={selectedWarehouse}
          setSelectedWarehouse={setSelectedWarehouse}
          setShowNewCustomerModal={setShowNewCustomerModal}
          setShowNewVendorModal={setShowNewVendorModal}
          setShowNewWarehouseModal={setShowNewWarehouseModal}
          setShowAddAddressModal={setShowAddAddressModal}
          setAddressModalType={setAddressModalType}
          setAddressDataToEdit={setAddressDataToEdit}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
        />
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Component Table</h3>
          <div style={{ position: 'relative', width: '300px' }} ref={searchRef}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px' }}>
              <Search size={16} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search components to add..." 
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
                style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px' }}
              />
            </div>
            {showSuggestions && (
              <div style={suggestionsBoxStyle}>
                {filteredProducts.map(p => (
                  <div key={p.id} onClick={() => addComponent(p)} style={suggestionItemStyle}>
                    <strong>{p.sku}</strong> - {p.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ ...thStyle, width: '50px' }}>S.No.</th>
                <th style={{ ...thStyle, width: '100px' }}>SKU</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...thStyle, width: '95px' }}>Material Qty</th>
                <th style={{ ...thStyle, width: '80px' }}>New</th>
                <th style={{ ...thStyle, width: '80px' }}>Rental</th>
                <th style={{ ...thStyle, width: '90px' }}>Repairable</th>
                <th style={{ ...thStyle, width: '110px' }}>Non Repairable</th>
                <th style={{ ...thStyle, width: '90px' }}>Weight (kg)</th>
                <th style={{ ...thStyle, width: '100px' }}>Total Weight</th>
                <th style={{ ...thStyle, width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {formData.components.map((c, index) => {
                const rowTotalQty = (parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0) + parseInt(c.repairableQty || 0) + parseInt(c.nonRepairableQty || 0));
                const totalRowWeight = ( rowTotalQty * (parseFloat(c.weight) || 0) ).toFixed(2);
                
                return (
                  <tr key={c.id || index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{c.sku}</td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>{c.name}</td>
                    <td style={{ ...tdStyle, fontWeight: '700', color: '#64748b' }}>{c.originalPcQuantity || 0}</td>
                    <td style={tdStyle}>
                        <input type="number" value={c.newQty} onChange={(e) => handleComponentChange(index, 'newQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" value={c.rentalQty} onChange={(e) => handleComponentChange(index, 'rentalQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" value={c.repairableQty} onChange={(e) => handleComponentChange(index, 'repairableQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" value={c.nonRepairableQty} onChange={(e) => handleComponentChange(index, 'nonRepairableQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" step="0.01" value={c.weight} onChange={(e) => handleComponentChange(index, 'weight', e.target.value)} readOnly={isSalesRentalMic} style={{ ...tableInputStyle, backgroundColor: isSalesRentalMic ? '#f8fafc' : '#fff' }} />
                    </td>
                    <td style={tdStyle}>{totalRowWeight}</td>
                    <td style={tdStyle}>
                      <button onClick={() => deleteComponent(index)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                <td colSpan="3" style={{ ...tdStyle, textAlign: 'right' }}>TOTAL</td>
                <td style={tdStyle}>{formData.components.reduce((sum, c) => sum + (Number(c.originalPcQuantity) || 0), 0)}</td>
                <td style={tdStyle}>{totals.newQty}</td>
                <td style={tdStyle}>{totals.rentalQty}</td>
                <td style={tdStyle}>{totals.repairableQty}</td>
                <td style={tdStyle}>{totals.nonRepairableQty}</td>
                <td></td>
                <td style={tdStyle}>{totals.weight.toFixed(2)} kg</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><UserCheck size={18} /> Staff & Verification</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={labelStyle}>Prepared By</label>
                    <select name="preparedBy" value={formData.preparedBy} onChange={handleChange} style={getFieldStyle('preparedBy', inputStyle)}>
                        <option value="">Select...</option>
                        <option value="Rajesh">Rajesh</option>
                        <option value="Ghanshyam">Ghanshyam</option>
                        <option value="Ankit">Ankit</option>
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Quantity Verified By</label>
                    <select name="quantityVerifiedBy" value={formData.quantityVerifiedBy} onChange={handleChange} style={getFieldStyle('quantityVerifiedBy', inputStyle)}>
                        <option value="">Select...</option>
                        <option value="Prashant">Prashant</option>
                        <option value="Shakti">Shakti</option>
                        <option value="Govind">Govind</option>
                    </select>
                </div>
                <div style={{ gridColumn: 'span 2', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                    <label style={labelStyle}><Truck size={14} /> Vehicle Details</label>
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Number</label>
                    <input type="text" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} placeholder="e.g. RJ-14-GA-1234" style={getFieldStyle('vehicleNumber', inputStyle)} />
                </div>
                <div>
                    <label style={labelStyle}>Date & Time</label>
                    <input type="datetime-local" name="vehicleDateTime" value={formData.vehicleDateTime} onChange={handleChange} style={getFieldStyle('vehicleDateTime', inputStyle)} />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle In Time</label>
                    <input type="time" name="vehicleInTime" value={formData.vehicleInTime} onChange={handleChange} style={getFieldStyle('vehicleInTime', inputStyle)} />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Out Time</label>
                    <input type="time" name="vehicleOutTime" value={formData.vehicleOutTime} onChange={handleChange} style={getFieldStyle('vehicleOutTime', inputStyle)} />
                </div>
            </div>
        </div>

        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><ClipboardCheck size={18} /> Declaration & Acknowledgement</h3>
            <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', margin: 0 }}>
                    "I hereby confirm that the material has been physically verified as per the Invoice and Challan. 
                    The received quantity matches the documents, and the quality has been checked and found satisfactory."
                </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={labelStyle}>Verified By</label>
                    <select name="verifiedByFinance" value={formData.verifiedByFinance} onChange={handleChange} style={getFieldStyle('verifiedByFinance', inputStyle)}>
                        <option value="">Select...</option>
                        <option value="Shakti">Shakti</option>
                        <option value="Prashant">Prashant</option>
                        <option value="Govind">Govind</option>
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Date</label>
                    <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleChange} style={getFieldStyle('declarationDate', inputStyle)} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Signature / Verification Remark</label>
                    <input type="text" placeholder="Type name for digital signature..." style={inputStyle} />
                </div>
            </div>
        </div>

      </div>
      
      {/* Completion Details from PC (Read-Only) */}
      {false && (formData.completionRemarks || formData.completionFiles.length > 0) && (
        <div style={{ marginTop: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
            Pickup Completion Details
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Completion Remarks</div>
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', minHeight: '90px', whiteSpace: 'pre-wrap', color: '#0f172a', fontSize: '13px' }}>
                {formData.completionRemarks || '—'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Attached Files</div>
              {(formData.completionFiles && formData.completionFiles.length > 0) ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {formData.completionFiles.map((f, idx) => (
                    <a
                      key={`${f.url || f.name || 'file'}_${idx}`}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ 
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', 
                        border: '1px solid #e2e8f0', borderRadius: '10px', textDecoration: 'none', 
                        color: '#0f172a', backgroundColor: '#ffffff',
                        transition: 'background-color 0.2s ease-in-out'
                      }}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name || 'Attachment'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{f.type || 'file'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', color: '#64748b', fontSize: '13px' }}>
                  No files attached.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
          MIC Completion Details
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>MIC Upload</div>
            <MultiFileUpload
              value={selectedMicCompletionFile ? [selectedMicCompletionFile] : (formData.micCompletionFiles || [])}
              onFilesChange={(files) => {
                const nextFile = files[0] || null;
                setSelectedMicCompletionFile(nextFile instanceof File ? nextFile : null);
                setValidationErrors(prev => {
                  if (!prev.micCompletionFiles) return prev;
                  const next = { ...prev };
                  delete next.micCompletionFiles;
                  return next;
                });
                if (files.length === 0) {
                  setFormData((prev) => ({ ...prev, micCompletionFiles: [] }));
                }
              }}
              multiple={false}
              maxFiles={1}
              disabled={isSaving}
              uploading={isSaving}
              helperText="Single file only."
              allowedTypes={['image/jpeg', 'image/png', 'image/webp', 'application/pdf']}
              externalError={validationErrors.micCompletionFiles ? 'MIC upload is required before confirming.' : ''}
            />
          </div>

          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>MIC Remarks</div>
            <textarea
              name="micCompletionRemarks"
              value={formData.micCompletionRemarks || ''}
              onChange={handleChange}
              placeholder="Enter MIC completion remarks..."
              style={getFieldStyle('micCompletionRemarks', { ...inputStyle, minHeight: '120px' })}
            />
          </div>
        </div>
      </div>

      {/* Modals for Customer and Address Management */}
      <NewCustomerModal
        show={showNewCustomerModal}
        onClose={() => setShowNewCustomerModal(false)}
        onSave={(savedCust) => {
            setAllCustomers(prev => {
                const existingIndex = prev.findIndex(c => c.id === savedCust.id);
                if (existingIndex > -1) {
                    return prev.map(c => c.id === savedCust.id ? savedCust : c);
                }
                return [...prev, savedCust];
            });
            selectCustomer(savedCust);
            setShowNewCustomerModal(false);
        }}
        db={db}
        isMobile={false} 
      />

      {selectedCustomer && (
        <AddAddressModal
            show={showAddAddressModal}
            onClose={() => setShowAddAddressModal(false)}
            onSave={(savedAddress, updatedAddresses) => {
                const updatedCustomer = { ...selectedCustomer, addresses: updatedAddresses };
                setSelectedCustomer(updatedCustomer); 
                setAllCustomers(prev => prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)); 
                setFormData(prev => ({ ...prev, shippingAddress: formatAddress(savedAddress) }));
                setShowAddAddressModal(false);
            }}
            db={db}
            customer={selectedCustomer}
            addressType={addressModalType}
            addressData={addressDataToEdit}
            isMobile={false} 
            hideBillingOption={true}
        />
      )}

      <NewWarehouseModal
        show={showNewWarehouseModal}
        onClose={() => setShowNewWarehouseModal(false)}
        onSave={(savedWh) => {
          setAllWarehouses(prev => [...prev, savedWh]);
          selectWarehouse(savedWh);
          setShowNewWarehouseModal(false);
        }}
        db={db}
      />

      <NewVendorModal
        show={showNewVendorModal}
        onClose={() => setShowNewVendorModal(false)}
        onSave={(savedVen) => {
          setAllVendors(prev => [...prev, savedVen]);
          selectVendor(savedVen);
          setShowNewVendorModal(false);
        }}
        db={db}
      />
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const tableInputStyle = { width: '60px', textAlign: 'center', padding: '5px', borderRadius: '4px', border: '1px solid #d1d5db' };
const saveBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' };
const draftBtnStyle = { ...saveBtnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', display: 'flex', alignItems: 'center', gap: '8px' };
const confirmBtnStyle = { ...saveBtnStyle, backgroundColor: '#059669', display: 'flex', alignItems: 'center', gap: '8px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 4, padding: 0 };
const errorInputStyle = { border: '1px solid #dc2626', boxShadow: '0 0 0 1px #dc2626', borderRadius: '6px' };

export default CreateMICPage;
